import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import EventIcon from "@mui/icons-material/Event";
import TuneIcon from "@mui/icons-material/Tune";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const BRAND = "#a388f3";
const BRAND_HOVER = "#8b6fe0";

const SCHEDULE_TYPES = [
  { value: "daily", label: "Every day", Icon: TodayOutlinedIcon },
  { value: "weekday", label: "Weekdays", Icon: DateRangeOutlinedIcon },
  { value: "interval", label: "Repeating", Icon: AutorenewIcon },
  { value: "once", label: "Run once", Icon: EventIcon },
  { value: "custom", label: "Advanced", Icon: TuneIcon },
];

const INTERVAL_UNITS = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

const UNIT_MS = { minutes: 60000, hours: 3600000, days: 86400000 };

const TIMEZONES = [
  { value: "America/Los_Angeles", label: "Pacific Time" },
  { value: "America/Denver", label: "Mountain Time" },
  { value: "America/Chicago", label: "Central Time" },
  { value: "America/New_York", label: "Eastern Time" },
  { value: "America/Phoenix", label: "Arizona Time" },
  { value: "America/Anchorage", label: "Alaska Time" },
  { value: "Pacific/Honolulu", label: "Hawaii Time" },
  { value: "UTC", label: "UTC" },
];

function tzLabel(tz) {
  const found = TIMEZONES.find((t) => t.value === tz);
  if (found) return found.label;
  return tz.split("/").pop().replace(/_/g, " ");
}

function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles";
  } catch {
    return "America/Los_Angeles";
  }
}

function to24Hour(hour12, ampm) {
  if (ampm === "AM") return hour12 === 12 ? 0 : hour12;
  return hour12 === 12 ? 12 : hour12 + 12;
}

function from24Hour(h24) {
  if (h24 === 0) return { hour12: 12, ampm: "AM" };
  if (h24 < 12) return { hour12: h24, ampm: "AM" };
  if (h24 === 12) return { hour12: 12, ampm: "PM" };
  return { hour12: h24 - 12, ampm: "PM" };
}

function formatTime(hour12, minute, ampm) {
  return `${hour12}:${String(minute).padStart(2, "0")} ${ampm}`;
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function buildSchedule(form) {
  switch (form.scheduleType) {
    case "daily": {
      const h = to24Hour(form.hour12, form.ampm);
      return { kind: "cron", expr: `${form.minute} ${h} * * *`, tz: form.tz };
    }
    case "weekday": {
      const h = to24Hour(form.hour12, form.ampm);
      return { kind: "cron", expr: `${form.minute} ${h} * * 1-5`, tz: form.tz };
    }
    case "interval":
      return { kind: "every", everyMs: (form.intervalValue || 1) * UNIT_MS[form.intervalUnit] };
    case "once": {
      if (!form.atDate) return null;
      const h = to24Hour(form.atHour12, form.atAmpm);
      const dateStr = `${form.atDate}T${String(h).padStart(2, "0")}:${String(form.atMinute).padStart(2, "0")}:00`;
      return { kind: "at", at: new Date(dateStr).toISOString() };
    }
    case "custom":
      return { kind: "cron", expr: form.cronExpr, tz: form.tz };
    default:
      return null;
  }
}

function buildSummary(form) {
  const schedule = buildSchedule(form);
  if (!schedule) return null;

  let desc = "";
  const needsTz = form.scheduleType !== "interval";

  switch (form.scheduleType) {
    case "daily":
      desc = `every day at ${formatTime(form.hour12, form.minute, form.ampm)}`;
      break;
    case "weekday":
      desc = `every weekday (Mon\u2013Fri) at ${formatTime(form.hour12, form.minute, form.ampm)}`;
      break;
    case "interval": {
      const v = form.intervalValue || 1;
      const unitLabel = form.intervalUnit === "minutes" ? "minute" : form.intervalUnit === "hours" ? "hour" : "day";
      desc = v === 1 ? `every ${unitLabel}` : `every ${v} ${unitLabel}s`;
      break;
    }
    case "once": {
      if (!form.atDate) return null;
      try {
        const d = new Date(form.atDate + "T00:00:00");
        desc = `once on ${d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} at ${formatTime(form.atHour12, form.atMinute, form.atAmpm)}`;
      } catch {
        return null;
      }
      break;
    }
    case "custom":
      desc = `on custom schedule: ${form.cronExpr || "..."}`;
      break;
    default:
      return null;
  }

  const tz = needsTz ? ` ${tzLabel(form.tz)}` : "";
  return `Your AI agent will run ${desc}${tz}`;
}

function getDefaultForm(job) {
  const browserTz = detectTimezone();

  if (!job) {
    return {
      name: "",
      message: "",
      scheduleType: "daily",
      hour12: 9,
      minute: 0,
      ampm: "AM",
      tz: browserTz,
      showTzPicker: false,
      intervalValue: 1,
      intervalUnit: "hours",
      atDate: "",
      atHour12: 9,
      atMinute: 0,
      atAmpm: "AM",
      cronExpr: "",
    };
  }

  const base = {
    name: job.name || "",
    message: job.message || "",
    tz: job.schedule?.tz || browserTz,
    showTzPicker: false,
    hour12: 9,
    minute: 0,
    ampm: "AM",
    intervalValue: 1,
    intervalUnit: "hours",
    atDate: "",
    atHour12: 9,
    atMinute: 0,
    atAmpm: "AM",
    cronExpr: "",
    scheduleType: "daily",
  };

  if (!job.schedule) return base;

  switch (job.schedule.kind) {
    case "cron": {
      const parts = (job.schedule.expr || "").split(" ");
      if (parts.length >= 5) {
        const h24 = parseInt(parts[1], 10);
        const { hour12, ampm } = from24Hour(h24);
        base.minute = parseInt(parts[0], 10);
        base.hour12 = hour12;
        base.ampm = ampm;
        const dow = parts[4];
        if (dow === "1-5") {
          base.scheduleType = "weekday";
        } else if (dow === "*" && parts[2] === "*" && parts[3] === "*") {
          base.scheduleType = "daily";
        } else {
          base.scheduleType = "custom";
          base.cronExpr = job.schedule.expr;
        }
      }
      break;
    }
    case "every": {
      base.scheduleType = "interval";
      const ms = job.schedule.everyMs || 3600000;
      if (ms >= 86400000 && ms % 86400000 === 0) {
        base.intervalValue = ms / 86400000;
        base.intervalUnit = "days";
      } else if (ms >= 3600000 && ms % 3600000 === 0) {
        base.intervalValue = ms / 3600000;
        base.intervalUnit = "hours";
      } else {
        base.intervalValue = Math.round(ms / 60000);
        base.intervalUnit = "minutes";
      }
      break;
    }
    case "at": {
      base.scheduleType = "once";
      if (job.schedule.at) {
        const d = new Date(job.schedule.at);
        const { hour12, ampm } = from24Hour(d.getHours());
        base.atHour12 = hour12;
        base.atMinute = d.getMinutes();
        base.atAmpm = ampm;
        const y = d.getFullYear();
        const mo = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        base.atDate = `${y}-${mo}-${day}`;
      }
      break;
    }
    default:
      break;
  }

  return base;
}

// --- Inline styles ---

const selectStyle = {
  padding: "7px 8px",
  borderRadius: 6,
  border: "1px solid var(--border-default)",
  fontSize: 14,
  fontFamily: "Inter, sans-serif",
  backgroundColor: "var(--surface-card)",
  outline: "none",
  cursor: "pointer",
  color: "var(--text-body)",
  minWidth: 52,
};

const chipStyle = (selected) => ({
  padding: "7px 14px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  border: `1.5px solid ${selected ? BRAND : "var(--border-default)"}`,
  background: selected ? BRAND : "var(--surface-card)",
  color: selected ? "#fff" : "var(--text-body)",
  transition: "all 0.15s",
  display: "flex",
  alignItems: "center",
  gap: 5,
  whiteSpace: "nowrap",
  outline: "none",
  fontFamily: "Inter, sans-serif",
});

const labelStyle = {
  fontSize: 14,
  fontWeight: 500,
  color: "var(--text-secondary)",
  marginBottom: 8,
  fontFamily: "Inter, sans-serif",
};

const ampmBtnStyle = (active) => ({
  padding: "6px 10px",
  border: "none",
  background: active ? BRAND : "var(--surface-subtle)",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  color: active ? "#fff" : "var(--text-secondary)",
  transition: "all 0.15s",
  fontFamily: "Inter, sans-serif",
  outline: "none",
});

const linkBtnStyle = {
  background: "none",
  border: "none",
  color: BRAND,
  fontSize: 12,
  cursor: "pointer",
  textDecoration: "underline",
  padding: 0,
  fontFamily: "Inter, sans-serif",
};

// --- Sub-components ---

function TimePicker({ hour12, minute, ampm, onChangeField }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <select
        value={hour12}
        onChange={(e) => onChangeField("hour12", parseInt(e.target.value, 10))}
        style={selectStyle}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <span style={{ fontSize: 16, fontWeight: 600, color: "var(--text-body)" }}>:</span>
      <select
        value={minute}
        onChange={(e) => onChangeField("minute", parseInt(e.target.value, 10))}
        style={selectStyle}
      >
        {[0, 15, 30, 45].map((m) => (
          <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
        ))}
      </select>
      <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", border: "1px solid var(--border-default)" }}>
        <button type="button" onClick={() => onChangeField("ampm", "AM")} style={ampmBtnStyle(ampm === "AM")}>AM</button>
        <button type="button" onClick={() => onChangeField("ampm", "PM")} style={ampmBtnStyle(ampm === "PM")}>PM</button>
      </div>
    </div>
  );
}

// --- Main component ---

export default function ScheduledTaskForm({ open, onClose, onSave, editingJob }) {
  const [form, setForm] = useState(() => getDefaultForm(editingJob));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(getDefaultForm(editingJob));
      setError("");
    }
  }, [open, editingJob]);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const setMulti = (updates) => setForm((f) => ({ ...f, ...updates }));

  const summary = useMemo(() => buildSummary(form), [form]);

  const validate = () => {
    if (!form.name.trim()) return "Please give this task a name";
    if (!form.message.trim()) return "Please describe what the AI agent should do";

    const schedule = buildSchedule(form);
    if (!schedule) return "Please complete the schedule";

    if (schedule.kind === "every" && schedule.everyMs < 300000) {
      return "Minimum interval is 5 minutes";
    }
    if (schedule.kind === "at") {
      if (!schedule.at) return "Please pick a date and time";
      if (new Date(schedule.at) <= new Date()) return "Scheduled time must be in the future";
    }
    if (schedule.kind === "cron" && !schedule.expr.trim()) {
      return "Please enter a cron expression";
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setSaving(true);

    const schedule = buildSchedule(form);
    const payload = {
      name: form.name.trim(),
      message: form.message.trim(),
      schedule,
    };

    try {
      await onSave(payload, editingJob);
    } catch (e) {
      setError(e.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const needsTimezone = form.scheduleType !== "interval";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: 16, pb: 0.5 }}>
        {editingJob ? "Edit Scheduled Task" : "New Scheduled Task"}
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: "12px !important" }}>
        {error && (
          <Alert severity="error" sx={{ py: 0.5, fontSize: 12 }}>{error}</Alert>
        )}

        {/* Task Name */}
        <TextField
          label="Task Name"
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          size="small"
          fullWidth
          placeholder="e.g. Morning rate check"
        />

        {/* Agent Instructions */}
        <TextField
          label="What should the AI agent do?"
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          placeholder={"e.g. Go to wholesalelender.com and screenshot today's\nrate sheet for 30-year fixed conventional loans"}
        />

        {/* Schedule Type Cards */}
        <div>
          <div style={labelStyle}>How often should this run?</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {SCHEDULE_TYPES.map(({ value, label, Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => set("scheduleType", value)}
                style={chipStyle(form.scheduleType === value)}
              >
                <Icon sx={{ fontSize: 14 }} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Time picker for daily / weekday */}
        {(form.scheduleType === "daily" || form.scheduleType === "weekday") && (
          <div>
            <div style={labelStyle}>What time?</div>
            <TimePicker
              hour12={form.hour12}
              minute={form.minute}
              ampm={form.ampm}
              onChangeField={(field, value) => set(field, value)}
            />
          </div>
        )}

        {/* Interval: number + unit picker */}
        {form.scheduleType === "interval" && (
          <div>
            <div style={labelStyle}>Run every:</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 14, color: "var(--text-body)", fontFamily: "Inter, sans-serif" }}>Every</span>
              <input
                type="number"
                min={1}
                max={999}
                value={form.intervalValue}
                onChange={(e) => set("intervalValue", Math.max(1, parseInt(e.target.value, 10) || 1))}
                style={{ ...selectStyle, width: 64, textAlign: "center" }}
              />
              <select
                value={form.intervalUnit}
                onChange={(e) => set("intervalUnit", e.target.value)}
                style={{ ...selectStyle, minWidth: 100 }}
              >
                {INTERVAL_UNITS.map((u) => (
                  <option key={u.value} value={u.value}>{u.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* One-time: date + time picker */}
        {form.scheduleType === "once" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <div style={labelStyle}>Pick a date</div>
              <input
                type="date"
                value={form.atDate}
                min={todayStr()}
                onChange={(e) => set("atDate", e.target.value)}
                style={{ ...selectStyle, minWidth: 150 }}
              />
            </div>
            <div>
              <div style={labelStyle}>Pick a time</div>
              <TimePicker
                hour12={form.atHour12}
                minute={form.atMinute}
                ampm={form.atAmpm}
                onChangeField={(field, value) => {
                  const mapped = { hour12: "atHour12", minute: "atMinute", ampm: "atAmpm" };
                  set(mapped[field] || field, value);
                }}
              />
            </div>
          </div>
        )}

        {/* Custom cron expression */}
        {form.scheduleType === "custom" && (
          <TextField
            label="Cron Expression"
            value={form.cronExpr}
            onChange={(e) => set("cronExpr", e.target.value)}
            size="small"
            fullWidth
            placeholder="0 9 * * 1-5"
            helperText="5-field format: minute hour day-of-month month day-of-week"
          />
        )}

        {/* Timezone display */}
        {needsTimezone && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-secondary)" }}>
            <AccessTimeIcon sx={{ fontSize: 14, color: "var(--text-muted)" }} />
            <span>{tzLabel(form.tz)}</span>
            {!form.showTzPicker ? (
              <button type="button" onClick={() => set("showTzPicker", true)} style={linkBtnStyle}>
                Change
              </button>
            ) : (
              <select
                value={form.tz}
                onChange={(e) => setMulti({ tz: e.target.value, showTzPicker: false })}
                style={{ ...selectStyle, fontSize: 12, padding: "4px 6px" }}
              >
                {TIMEZONES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Summary box */}
        {summary && form.name.trim() && (
          <div style={{
            background: `${BRAND}0D`,
            borderLeft: `3px solid ${BRAND}`,
            borderRadius: "0 8px 8px 0",
            padding: "10px 14px",
            fontSize: 12,
            fontFamily: "Inter, sans-serif",
            color: "var(--text-body)",
            lineHeight: 1.6,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>{summary}</div>
            {form.message.trim() && (
              <div style={{ color: "var(--text-secondary)", fontStyle: "italic" }}>
                &ldquo;{form.message.trim().substring(0, 100)}{form.message.trim().length > 100 ? "..." : ""}&rdquo;
              </div>
            )}
          </div>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small" sx={{ color: "var(--text-secondary)", textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={saving}
          sx={{
            backgroundColor: BRAND,
            "&:hover": { backgroundColor: BRAND_HOVER },
            textTransform: "none",
          }}
        >
          {saving ? (
            <CircularProgress size={16} sx={{ color: "#fff" }} />
          ) : editingJob ? (
            "Save Changes"
          ) : (
            "Create Task"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
